import React from 'react';
import { AlertCircle, Copy, X, Key } from 'lucide-react';

const LicenseModal = ({ isOpen, onClose, licenseKey, licenseType = 'paid', expirationDate }) => {
  if (!isOpen) return null;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(licenseKey);
    alert('License key copied to clipboard!');
  };

  const formatExpirationDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isTrialLicense = licenseType === 'trial' || licenseKey.includes('TRIAL');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full m-4">
        {/* Header */}
        <div className={`${isTrialLicense ? 'bg-gradient-to-r from-green-500 to-blue-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'} text-white p-6 rounded-t-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 p-2 rounded-full mr-3">
                <Key className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">
                  {isTrialLicense ? 'Trial License Ready!' : 'License Activated!'}
                </h3>
                <p className="text-sm opacity-90">
                  {isTrialLicense ? 'Your 7-day trial has started' : 'Your paid license is now active'}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className={`${isTrialLicense ? 'bg-green-100 dark:bg-green-900' : 'bg-amber-100 dark:bg-amber-900'} p-4 rounded-lg mb-4`}>
              <p className={`text-sm ${isTrialLicense ? 'text-green-800 dark:text-green-200' : 'text-amber-800 dark:text-amber-200'}`}>
                <strong>Congratulations!</strong> Your {isTrialLicense ? 'trial' : 'paid'} license has been {isTrialLicense ? 'generated' : 'activated'} successfully.
              </p>
            </div>
          </div>

          {/* License Key Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your {isTrialLicense ? 'Trial' : 'Professional'} License Key:
            </label>
            <div className="relative">
              <input
                type="text"
                value={licenseKey}
                readOnly
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              />
              <button
                onClick={handleCopyToClipboard}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                title="Copy to clipboard"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Click the copy icon to copy your license key to clipboard
            </p>
          </div>

          {/* Expiration Info */}
          {expirationDate && (
            <div className="mb-6">
              <div className={`${isTrialLicense ? 'bg-blue-50 dark:bg-blue-900' : 'bg-amber-50 dark:bg-amber-900'} p-4 rounded-lg`}>
                <div className="flex items-center mb-2">
                  <AlertCircle className={`w-5 h-5 ${isTrialLicense ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'} mr-2`} />
                  <h4 className={`font-medium ${isTrialLicense ? 'text-blue-800 dark:text-blue-200' : 'text-amber-800 dark:text-amber-200'}`}>
                    {isTrialLicense ? 'Trial Period' : 'License Period'}
                  </h4>
                </div>
                <p className={`text-sm ${isTrialLicense ? 'text-blue-700 dark:text-blue-300' : 'text-amber-700 dark:text-amber-300'}`}>
                  Your license expires on <strong>{formatExpirationDate(expirationDate)}</strong>
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                window.location.href = '/downloads';
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Download Torque
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Close
            </button>
          </div>

          {/* Note */}
          <div className={`mt-4 p-3 ${isTrialLicense ? 'bg-yellow-50 dark:bg-yellow-900' : 'bg-green-50 dark:bg-green-900'} rounded-md`}>
            <p className={`text-xs ${isTrialLicense ? 'text-yellow-800 dark:text-yellow-200' : 'text-green-800 dark:text-green-200'}`}>
              <strong>Important:</strong> Save this license key securely. You'll need it to activate the Torque desktop application.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseModal;